/**
 * The address the provider posts back to, and the secret it signs with.
 *
 * Its own file rather than another block in `MailChannelPanel.tsx`, which is
 * already at the length where a reader stops finding things. This is one
 * question - "how do we learn that a letter did not arrive" - and it is the
 * question the card was missing.
 *
 * WHY IT IS NOT OPTIONAL DECORATION. Without this webhook configured at the
 * provider, nothing in the CRM ever learns about a hard bounce or a spam
 * report, the ledger's `bounced` and `complained` columns stay empty, and the
 * campaign sender's circuit breaker measures a clean record that is really no
 * record at all. So the hint says that in words rather than leaving the field
 * looking like an advanced option.
 *
 * The URL is built by the SERVER from the public origin. Building it here from
 * `window.location` would be right on this screen and wrong for anybody reading
 * the settings through a different host, and the failure - a webhook URL a
 * provider can never reach - is invisible until the first bounce is missed.
 */
import { useT } from "../../../../i18n/I18nProvider";
import type { MailChannelView } from "../../../../lib/mail";
import { CopyPill, Field, FieldGroup, Note, SecretPill } from "../panel/fields";

export function MailChannelWebhook({
  channel,
  secret,
  onSecret,
}: {
  channel: MailChannelView;
  secret: string;
  onSecret: (v: string) => void;
}) {
  const t = useT();

  return (
    <FieldGroup title={t("settings.channels.webhookTitle")}>
      <Field label={t("settings.channels.webhookUrl")} hint={t("settings.channels.webhookHint")}>
        <CopyPill
          value={channel.webhookUrl}
          copyLabel={t("settings.mail.copy")}
          copiedLabel={t("settings.mail.copied")}
        />
      </Field>

      <Field
        label={t("settings.channels.webhookSecret")}
        hint={
          channel.webhookSecretSet && !secret
            ? t("settings.mail.passwordKept")
            : t("settings.channels.webhookSecretHint")
        }
        className="sm:max-w-[420px]"
      >
        <SecretPill
          value={secret}
          onChange={onSecret}
          placeholder={channel.webhookSecretSet ? "••••••••" : "whsec_..."}
        />
      </Field>

      {!channel.webhookSecretSet ? (
        <Note tone="mut">{t("settings.channels.webhookUnsigned")}</Note>
      ) : null}
    </FieldGroup>
  );
}
